import { TGetOrderResponse, TPageSettingsResponse } from '@p2p/services';
import {
  failStatuses,
  IN_DISPUTE,
  NOT_FOUND_PAYMENT,
  ORDER_STATUS,
  PROOF_PAGE,
  successStatuses,
} from '@p2p/shared/constants';

type ResolveProofRouteParams = {
  order: TGetOrderResponse;
  pageSettings: TPageSettingsResponse;
  // Проверка Cloudflare уже пройдена по этой заявке.
  isCheckedUser: boolean;
  // Чек по этой заявке уже отправлен с этого устройства (до того, как check_order
  // успел вернуть receipt_uploaded).
  isReceiptSent: boolean;
};

export const getProofPagePath = (orderId: string) => `${PROOF_PAGE}/${orderId}`;

/**
 * P2P-2880: на какой экран proof_page должна вести заявка в текущем состоянии.
 * Всегда абсолютный путь.
 *
 * Главное правило страницы: после отправки чека вернуться к реквизитам нельзя —
 * ни «Назад», ни перезагрузкой, ни прямой ссылкой. Поэтому для BRAND_NEW с чеком
 * единственный допустимый экран — поиск платежа.
 */
export const resolveProofRoute = ({
  order,
  pageSettings,
  isCheckedUser,
  isReceiptSent,
}: ResolveProofRouteParams): string => {
  const pagePath = getProofPagePath(order.id);

  if (successStatuses.includes(order.status)) return `${pagePath}/success`;

  if (order.status === IN_DISPUTE) return `${pagePath}/dispute/success`;

  if (failStatuses.includes(order.status)) return `${pagePath}/dispute`;

  if (order.status === ORDER_STATUS.REQUISITES_NOT_FOUND) {
    return `/${order.id}/${NOT_FOUND_PAYMENT}`;
  }

  if (order.receipt_uploaded || isReceiptSent) return `${pagePath}/search`;

  if (pageSettings.cloudflare && !isCheckedUser) return `${pagePath}/check-user`;

  return pagePath;
};

/**
 * Можно ли остаться на текущем адресе, хотя resolveProofRoute ведёт в другое место.
 * Оба исключения — переходы, которые фронт делает сам раньше, чем check_order вернёт
 * новый статус заявки.
 */
export const isProofRouteAllowed = (pathname: string, target: string, orderId: string) => {
  const path = pathname.replace(/\/+$/, '');
  const pagePath = getProofPagePath(orderId);

  if (path === target) return true;

  // Отмена: заявка уже переведена в TIME_OUT, показываем «Платёж успешно отменён» и
  // через несколько секунд уводим на сайт мерчанта (как на msp_page).
  if (path === `${pagePath}/canceled`) return true;

  // Диспут только что создан, а в кэше заявка ещё TIME_OUT/CANCELED_WITH_RECALCULATION.
  if (target === `${pagePath}/dispute` && path === `${pagePath}/dispute/success`) return true;

  return false;
};
